/**
 * Native Telegram chrome: BackButton, MainButton, haptics — SPEC §2/§6.
 *
 * Three hooks, one rule between them: **the native control belongs to nested
 * screens, never to a tab screen.** A tab screen already has the tab bar as its
 * navigation and its own primary action in the layout; adding Telegram's
 * MainButton there would put two primary controls on one view and make the
 * back gesture ambiguous.
 *
 * Outside Telegram every hook is a no-op, so the same components render on the
 * standalone web build with their own in-page buttons and the browser's back.
 * The SDK is loaded on the web too, so "inside" is `isInTelegram()`: calling it
 * there only filled the console with "not supported in version 6.0".
 */

import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { getTelegram, isInTelegram, type TelegramWebApp } from "@/boot/telegram";

/** The Telegram client this page runs in, or null on the web. */
function native(): TelegramWebApp | null {
  return isInTelegram() ? getTelegram() : null;
}

export interface MainButtonConfig {
  text: string;
  enabled: boolean;
  onClick: () => void;
  /** Shows the spinner and blocks input while a request is in flight. */
  progress?: boolean;
}

/**
 * Drive Telegram's MainButton from a screen. Pass `null` to hide it.
 *
 * The click handler is held in a ref and re-read on every press, so a config
 * that changes between renders (a mutation's `isPending`, a freshly loaded
 * entity) does not need the button to be torn down and re-registered — which
 * would visibly flicker on a slow client.
 */
export function useMainButton(config: MainButtonConfig | null): void {
  const handler = useRef<(() => void) | null>(null);
  // Written in an effect, not during render: a ref assignment in the render
  // body runs on every discarded render too (StrictMode, a suspended sibling),
  // which is exactly the class of bug refs are supposed to avoid.
  useEffect(() => {
    handler.current = config?.onClick ?? null;
  });

  const text = config?.text;
  const enabled = config?.enabled ?? false;
  const progress = config?.progress ?? false;
  const visible = config != null;

  useEffect(() => {
    const tg = native();
    if (!tg) return;
    const button = tg.MainButton;
    const onClick = () => handler.current?.();
    button.onClick(onClick);
    return () => {
      button.offClick(onClick);
      button.hideProgress();
      button.hide();
    };
  }, []);

  useEffect(() => {
    const tg = native();
    if (!tg) return;
    const button = tg.MainButton;
    if (!visible || !text) {
      button.hide();
      return;
    }
    button.setText(text);
    if (enabled) button.enable();
    else button.disable();
    if (progress) button.showProgress(true);
    else button.hideProgress();
    button.show();
  }, [visible, text, enabled, progress]);
}

/**
 * Show Telegram's BackButton on a nested screen and wire it to history.
 *
 * `fallback` is where to go when there is nothing to go back to — opening a
 * method by direct link leaves an empty history stack, and a back button that
 * does nothing is worse than no back button.
 */
export function useBackButton(fallback = "/"): void {
  const navigate = useNavigate();
  const target = useRef(fallback);
  useEffect(() => {
    target.current = fallback;
  }, [fallback]);

  useEffect(() => {
    const tg = native();
    if (!tg) return;
    const button = tg.BackButton;
    const onClick = () => {
      if (window.history.length > 1) navigate(-1);
      else navigate(target.current);
    };
    button.onClick(onClick);
    button.show();
    return () => {
      button.offClick(onClick);
      button.hide();
    };
  }, [navigate]);
}

/**
 * Haptics for the three moments worth one: a state change landed, something
 * was refused, a selection moved.
 *
 * Deliberately not fired on every tap — a device that buzzes constantly is one
 * people turn off, and then the feedback that mattered is gone too.
 */
export const haptics = {
  success(): void {
    native()?.HapticFeedback.notificationOccurred("success");
  },
  error(): void {
    native()?.HapticFeedback.notificationOccurred("error");
  },
  warning(): void {
    native()?.HapticFeedback.notificationOccurred("warning");
  },
  select(): void {
    native()?.HapticFeedback.selectionChanged();
  },
  tap(): void {
    native()?.HapticFeedback.impactOccurred("light");
  },
};
